// Section 16: Equal shares norm. Established by resolving food shortages with the equal-shares
// policy (4 uses in a row; reset by an unequal policy); lost after 8 seasons without
// reinforcement. Effect once established: any override to unequal rations costs 2x trust (the
// rationing phase's short-ration trust causes are doubled).
// Constants: config.normRules, config.norms['equal-shares'].
import type { Config } from '../../config';
import { mod } from '../../core/modifiers';
import { foodPrecedent } from '../../core/content-helpers';
import { defineNorm } from '../../core/registry';

const UNEQUAL_CAUSES: readonly string[] = ['rationed-short', 'rationed-short-deviation'];

export function equalShares(config: Config) {
  const c = config.norms['equal-shares'];

  return defineNorm({
    id: 'equal-shares',
    label: 'Equal shares',
    section: 'S16',
    establishAfter: config.normRules.establishAfter,
    decayAfter: config.normRules.decayAfter,
    reinforcedBy: (ctx, entry) => foodPrecedent(ctx, entry)?.policy === 'equal',
    contradictedBy(ctx, entry) {
      const p = foodPrecedent(ctx, entry);
      return p !== null && p.policy !== 'equal';
    },
    modifierTargets: ['trustChange'],
    modifierKeys: { trustChange: UNEQUAL_CAUSES },
    modifiers(ctx, target) {
      if (!ctx.state.norms['equal-shares'].established) return [];
      if (target.kind !== 'trustChange' || !UNEQUAL_CAUSES.includes(target.cause)) return [];
      return [mod.mul('norm:equal-shares', c.unequalOverrideTrustMul, 'breaks equal shares')];
    },
  });
}
